import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { EVENTS, pageName, rememberCameFromDemo, track } from '../../lib/analytics';

// The page last counted. Kept outside the hook: pages render Layout once
// while loading and again when loaded, which remounts it, and each mount
// would count the same page again.
let lastCounted = null;

/**
 * The demo, for the screens that show it: minutes left, and the two ways
 * out (sign up, or end it). Layout calls this once and hands the result to
 * the sidebar and the phone bar, so the countdown and the expiry run once.
 *
 * Both ways out log the demo user out first: a signed-in visitor is sent
 * away from /register, and the demo's data isn't theirs to keep.
 */
export function useDemoSession() {
  const isDemo = useAuthStore((s) => s.isDemo);
  const demoExpiresAt = useAuthStore((s) => s.demoExpiresAt);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [minutesLeft, setMinutesLeft] = useState(null);

  const leave = useCallback(async (to) => {
    lastCounted = null;
    rememberCameFromDemo();
    await logout();
    navigate(to);
  }, [logout, navigate]);

  const endDemo = useCallback((reason) => {
    track(EVENTS.DEMO_ENDED, { reason });
    return leave(`/demo-ended?reason=${reason}`);
  }, [leave]);

  const signUp = useCallback((source) => {
    track(EVENTS.DEMO_SIGNUP_CLICKED, { source });
    return leave('/register');
  }, [leave]);

  useEffect(() => {
    if (!isDemo || pathname === lastCounted) return;
    lastCounted = pathname;
    track(EVENTS.DEMO_PAGE_VIEWED, { page: pageName(pathname) });
  }, [isDemo, pathname]);

  useEffect(() => {
    if (!isDemo || !demoExpiresAt) {
      setMinutesLeft(null);
      return undefined;
    }
    const remaining = () => Math.max(0, Math.floor((new Date(demoExpiresAt) - new Date()) / 60000));
    setMinutesLeft(remaining());
    const interval = setInterval(() => {
      const left = remaining();
      setMinutesLeft(left);
      if (left <= 0) endDemo('expired');
    }, 60000);
    return () => clearInterval(interval);
  }, [isDemo, demoExpiresAt, endDemo]);

  return { isDemo, minutesLeft, signUp, endDemo };
}

/** "52 min left", or nothing before the first tick. */
export function demoTimeLabel(minutesLeft) {
  if (minutesLeft === null) return '';
  return minutesLeft > 0 ? `${minutesLeft} min left` : 'ending now';
}
